import React from 'react';
import { X, Download, ExternalLink, Clock, User, HardDrive, FileText, Mail } from 'lucide-react';

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    path: string;
    description?: string;
    is_legacy: number;
    created_at: string;
    user?: { // Make user optional with ?
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
    temporaryUrl?: string;
  }

interface MediaViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  media: Media;
}

const MediaViewModal: React.FC<MediaViewModalProps> = ({
  isOpen,
  onClose,
  media
}) => {
  if (!isOpen) return null;

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes?.toFixed(2) + ' MB';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' GB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' TB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' TB';
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Media Preview
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Viewing file: <span className="font-medium">{media.file_name}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Media preview */}
                <div className="col-span-2 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '300px' }}>
                  {media.file_type === 'image' && (
                    <img 
                      src={media.temporaryUrl} 
                      alt={media.file_name} 
                      className="max-w-full max-h-96 object-contain"
                    />
                  )}
                  
                  {media.file_type === 'video' && (
                    <video 
                      src={media.temporaryUrl} 
                      controls 
                      className="max-w-full max-h-96"
                    >
                      Your browser does not support video playback.
                    </video>
                  )}
                  
                  {media.file_type === 'audio' && (
                    <audio 
                      src={media.temporaryUrl} 
                      controls 
                      className="w-full"
                    >
                      Your browser does not support audio playback.
                    </audio>
                  )}
                  
                  {media.file_type === 'document' && (
                    <div className="text-center p-4">
                      <FileText className="h-16 w-16 text-blue-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Preview not available for documents</p>
                      <a 
                        href={media.temporaryUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open in New Tab
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Media details */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">File Information</h4>
                    <div className="mt-2 bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                      <div className="flex items-start">
                        <FileText className="mt-0.5 h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Name</p>
                          <p className="text-sm font-medium text-gray-900">{media.file_name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <HardDrive className="mt-0.5 h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Size</p>
                          <p className="text-sm font-medium text-gray-900">{formatFileSize(media.file_size)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Clock className="mt-0.5 h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Upload Date</p>
                          <p className="text-sm font-medium text-gray-900">{formatDate(media.created_at)}</p>
                        </div>
                      </div>
                      
                      {media.is_legacy === 1 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                          <p className="text-xs font-medium text-yellow-800">
                            This is a legacy file
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Owner Information</h4>
                    <div className="mt-2 bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                      <div className="flex items-start">
                        <User className="mt-0.5 h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Name</p>
                          <p className="text-sm font-medium text-gray-900">
                            {media?.user?.first_name} {media?.user?.last_name}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Mail className="mt-0.5 h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium text-gray-900">{media?.user?.email}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {media.description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Description</h4>
                      <div className="mt-2 bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-700">{media.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <a
              href={media.temporaryUrl}
              download={media.file_name}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </a>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaViewModal;